import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CriticalEventDialog = ({ isOpen, onClose, patientId, onUpdate, editingEvent = null }) => {
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    time: format(new Date(), 'HH:mm'),
    title: '',
    notes: '',
  });

  useEffect(() => {
    if (editingEvent) {
      setFormData({
        date: editingEvent.date || format(new Date(), 'yyyy-MM-dd'),
        time: editingEvent.time || format(new Date(), 'HH:mm'),
        title: editingEvent.title || '',
        notes: editingEvent.notes || '',
      });
    } else {
      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm'),
        title: '',
        notes: '',
      });
    }
  }, [editingEvent, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (editingEvent) {
        await axios.put(
          `${API}/critical-events/${editingEvent.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Critical event updated');
      } else {
        await axios.post(
          `${API}/critical-events`,
          { ...formData, patient_id: patientId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Critical event recorded');
      }
      onClose();
      onUpdate();
    } catch (error) {
      toast.error(editingEvent ? 'Failed to update' : 'Failed to record critical event');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingEvent ? 'Edit Critical Event' : 'Record Critical Event'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Date *</label>
              <input
                type="date"
                className="input w-full"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                max={format(new Date(), 'yyyy-MM-dd')}
                min={format(new Date(new Date().setMonth(new Date().getMonth() - 4)), 'yyyy-MM-dd')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Time *</label>
              <input
                type="time"
                className="input w-full"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              className="input w-full"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Emergency hospital visit, Severe reaction"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              className="input w-full"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional details about the critical event..."
              rows="4"
            />
          </div>

          <button type="submit" className="btn btn-primary w-full">
            {editingEvent ? 'Update' : 'Record'} Critical Event
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CriticalEventDialog;
